<template>
  <div class="p-4 lg:p-8">
    <div class="surface-card p-4 shadow-sm border border-iron rounded-lg">
      
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-5">
        <div>
          <h2 class="text-2xl font-bold text-secondary">Materiais Comerciais</h2>
          <p class="text-sm text-gray-500">Gerencie os pacotes e a biblioteca de materiais disponíveis para venda.</p>
        </div>
        
        <div class="flex items-center gap-2">
          <Button 
            label="Biblioteca de Arquivos" 
            icon="pi pi-folder" 
            class="p-button-outlined p-button-secondary px-4 text-sm" 
            @click="openAllMaterialsModal()" 
          />
          <Button 
            label="Novo Material (Arquivo)" 
            icon="pi pi-upload" 
            class="p-button-outlined p-button-primary px-4 text-sm" 
            @click="openNewMaterial()" 
          />
          <Button 
            label="Novo Pacote" 
            icon="pi pi-folder-plus" 
            class="bg-purple-suit-forced border-none px-4 text-sm text-white" 
            @click="openGroupModal()" 
          />
        </div>
      </div>

      <Divider />

      <LoadingDialog :visible="loading" />

      <template v-if="!loading">
        <div v-if="groups.length === 0" class="text-center py-8 text-gray-500">
          Nenhum pacote cadastrado no momento.
        </div>

        <div v-else class="flex flex-col gap-6">
          <div 
            v-for="group in groups" 
            :key="group.id" 
            class="border border-iron rounded-xl p-5 bg-white shadow-xs transition-all hover:shadow-md"
          >
            <div class="flex flex-col md:flex-row md:items-center justify-between pb-4 mb-4 border-b border-gray-100 gap-2">
              <div>
                <div class="flex items-center gap-2">
                  <h3 class="text-lg font-bold text-secondary">{{ group.title }}</h3>
                  <span class="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full font-semibold border border-purple-200">
                    R$ {{ Number(group.price || 0).toFixed(2) }}
                  </span>
                </div>
                <p v-if="group.description" class="text-xs text-gray-500 mt-1">{{ group.description }}</p>
              </div>

              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-400 font-medium mr-2">
                  {{ group.sellingMaterials?.length || 0 }} {{ group.sellingMaterials?.length === 1 ? 'arquivo' : 'arquivos' }}
                </span>

                <Button 
                  icon="pi pi-link" 
                  label="Gerenciar Arquivos"
                  class="p-button-sm p-button-outlined p-button-secondary text-xs" 
                  v-tooltip.top="'Vincular/Desvincular Materiais'"
                  @click="openGroupModal(group)" 
                />
                <Button 
                  icon="pi pi-pencil" 
                  class="p-button-text p-button-secondary p-button-sm" 
                  v-tooltip.top="'Editar Pacote'"
                  @click="openGroupModal(group)" 
                />
                <Button 
                  icon="pi pi-trash" 
                  class="p-button-text p-button-danger p-button-sm" 
                  v-tooltip.top="'Excluir Pacote'"
                  @click="confirmDeleteGroup(group)" 
                />
              </div>
            </div>

            <MaterialListComponent
              :value="group.sellingMaterials || []" 
              :loading="loading" 
              :downloading-ids="downloadingIds"
              @edit="editMaterial"
              @delete="confirmDelete"
              @download="downloadFile"
            />
          </div>
        </div>

        <div v-if="unassignedMaterials.length > 0" class="mt-8 border-t border-iron pt-6">
          <div class="flex justify-between items-center mb-4">
            <div>
              <h3 class="text-lg font-bold text-secondary">Arquivos Avulsos (Sem Pacote)</h3>
              <p class="text-xs text-gray-500">Materiais cadastrados que ainda não estão vinculados a nenhum pacote comercial.</p>
            </div>
            <span class="text-xs font-semibold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-md border border-amber-200">
              {{ unassignedMaterials.length }} {{ unassignedMaterials.length === 1 ? 'arquivo avulso' : 'arquivos avulsos' }}
            </span>
          </div>

          <MaterialListComponent
            :value="unassignedMaterials" 
            :loading="loading" 
            :downloading-ids="downloadingIds"
            @edit="editMaterial"
            @delete="confirmDelete"
            @download="downloadFile"
          />
        </div>
      </template>
    </div>

    <Dialog 
      v-model:visible="allMaterialsDialog" 
      :style="{ width: '850px' }" 
      header="Biblioteca Geral de Materiais" 
      :modal="true" 
      class="p-fluid"
      :closeOnEscape="true"
    >
      <div class="flex flex-col gap-4 mt-2">
        <div class="flex justify-between items-center gap-4">
          <InputText 
            v-model="searchFilter" 
            placeholder="Buscar por nome do arquivo..." 
            class="p-inputtext-sm w-full md:w-80"
          />
          <Button 
            label="Upload Novo Material" 
            icon="pi pi-upload" 
            class="p-button-sm p-button-primary text-xs shrink-0" 
            @click="openNewMaterial()" 
          />
        </div>

        <MaterialListComponent
          :value="filteredAllMaterials" 
          :loading="loading" 
          :downloading-ids="downloadingIds"
          @edit="editMaterial"
          @delete="confirmDelete"
          @download="downloadFile"
        />
      </div>

      <template #footer>
        <Button label="Fechar" class="p-button-text p-button-secondary" @click="allMaterialsDialog = false" />
      </template>
    </Dialog>

    <Dialog 
      v-model:visible="groupDialog" 
      :style="{ width: '600px' }" 
      :header="selectedGroup?.id ? 'Editar Pacote' : 'Novo Pacote'" 
      :modal="true" 
      class="p-fluid"
      :closeOnEscape="true"
    >
      <div class="flex flex-col gap-4 mt-2">
        <div class="field">
          <label for="group_title" class="font-semibold text-sm text-secondary mb-1 block">Título do Pacote *</label>
          <InputText id="group_title" class="w-full" v-model="groupForm.title" placeholder="Ex: Pacote Alfabetização 2026" required />
        </div>

        <div class="field">
          <label for="group_price" class="font-semibold text-sm text-secondary mb-1 block">Preço (R$) *</label>
          <InputNumber 
            id="group_price" 
            v-model="groupForm.price" 
            mode="currency" 
            currency="BRL" 
            locale="pt-BR" 
            class="w-full"
            placeholder="0,00"
          />
        </div>

        <div class="field">
          <label for="group_materials" class="font-semibold text-sm text-secondary mb-1 block">Selecione os Materiais Pertencentes</label>
          <MultiSelect 
            id="group_materials" 
            v-model="groupForm.selling_material_ids" 
            :options="allMaterialsOptions" 
            optionLabel="name" 
            optionValue="id" 
            placeholder="Escolha os materiais deste pacote" 
            filter 
            class="w-full"
            display="chip"
          >
            <template #option="slotProps">
              <div class="flex justify-between items-center w-full text-xs">
                <span class="font-medium text-gray-700">{{ slotProps.option.name }}</span>
              </div>
            </template>
          </MultiSelect>
        </div>

        <div class="field">
          <label for="group_description" class="font-semibold text-sm text-secondary mb-1 block">Descrição do Pacote</label>
          <Textarea id="group_description" class="w-full" v-model="groupForm.description" rows="3" placeholder="Resumo sobre os arquivos contidos..." autoResize />
        </div>

        <!-- SEÇÃO DE BENEFÍCIOS (TÓPICOS DETALHADOS) -->
        <div class="field">
          <div class="flex justify-between items-center mb-1">
            <label class="font-semibold text-sm text-secondary block">Benefícios / O que está incluso (por tópico)</label>
            <Button 
              label="Adicionar Benefício" 
              icon="pi pi-plus" 
              class="p-button-text p-button-sm text-xs p-0 text-purple-700" 
              @click="addDetailItem()" 
            />
          </div>
          <p class="text-xs text-gray-400 mb-2">Estes pontos serão exibidos em destaque na página do produto.</p>

          <div v-if="groupForm.benefits.length === 0" class="text-xs text-gray-400 italic p-3 border border-dashed rounded-md text-center bg-gray-50">
            Nenhum benefício adicionado. Clique no botão acima para adicionar tópicos.
          </div>

          <div v-else class="flex flex-col gap-2">
            <div 
              v-for="(item, index) in groupForm.benefits" 
              :key="index" 
              class="flex items-center gap-2"
            >
              <span class="text-purple-700 font-bold text-xs select-none">✦</span>
              <InputText 
                v-model="groupForm.benefits[index]" 
                placeholder="Ex: Acesso digital liberado imediatamente" 
                class="p-inputtext-sm w-full"
              />
              <Button 
                icon="pi pi-trash" 
                class="p-button-text p-button-danger p-button-sm shrink-0" 
                v-tooltip.top="'Remover tópico'"
                @click="removeDetailItem(index)" 
              />
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <Button label="Cancelar" class="p-button-text p-button-secondary" @click="groupDialog = false" :disabled="submittingGroup" />
          <Button :label="selectedGroup?.id ? 'Salvar Alterações' : 'Criar Pacote'" icon="pi pi-check" class="bg-purple-suit-forced border-none" @click="saveGroup" :loading="submittingGroup" />
        </div>
      </template>
    </Dialog>

    <!-- DIALOG MATERIAL INDIVIDUAL -->
    <Dialog 
      v-model:visible="materialDialog" 
      :style="{ width: '550px' }" 
      :header="selectedMaterial?.id ? 'Editar Material' : 'Novo Material Base'" 
      :modal="true" 
      class="p-fluid"
      :closeOnEscape="true"
      :draggable="false"
    >
      <div class="flex flex-col gap-4 mt-2">
        <div class="field">
          <label for="name" class="font-semibold text-sm text-secondary mb-1 block">Nome do Material *</label>
          <InputText id="name" class="w-full" v-model="form.name" placeholder="Ex: Fichário de Sílabas Simples.pdf" required />
        </div>

        <div class="field">
          <label for="description" class="font-semibold text-sm text-secondary mb-1 block">Descrição</label>
          <Textarea id="description" class="w-full" v-model="form.description" rows="3" placeholder="Detalhes sobre o conteúdo do arquivo..." autoResize />
        </div>

        <div class="field">
          <label class="font-semibold text-sm text-secondary mb-1 block">
            Arquivo do Material <span v-if="!selectedMaterial?.id">*</span>
          </label>
          
          <div 
            class="flex flex-col gap-2 p-4 border border-dashed rounded-lg text-center transition-colors duration-200 cursor-pointer"
            :class="isDragging ? 'border-primary bg-blue-50/30' : 'border-iron bg-gray-50'"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onFileDrop"
          >
            <input 
              type="file" 
              ref="fileInput" 
              id="fileUpload" 
              class="hidden" 
              accept=".pdf,.txt,.mp3,.mp4,image/*,.pptx,.docx,.zip,.rar" 
              @change="onFileSelect"
            />
            <label for="fileUpload" class="cursor-pointer flex flex-col items-center gap-2">
              <i class="pi pi-cloud-upload text-gray-400" style="font-size: 2rem"></i>
              <span class="text-sm text-gray-600 font-medium">
                {{ selectedFile ? selectedFile.name : 'Clique ou arraste o arquivo aqui para selecionar' }}
              </span>
              <span class="text-xs text-gray-400">
                Formatos aceitos: PDF, Office, Imagens, Áudio, Vídeo, ZIP, RAR
              </span>
            </label>
          </div>
          
          <p v-if="selectedMaterial?.id && !selectedFile" class="text-xs text-gray-500 mt-1 italic">
            * Deixe em branco se não desejar substituir o arquivo anexado atualmente.
          </p>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <Button label="Cancelar" class="p-button-text p-button-secondary" @click="materialDialog = false" :disabled="submitting" />
          <Button :label="selectedMaterial?.id ? 'Salvar Alterações' : 'Cadastrar'" icon="pi pi-check" class="bg-purple-suit-forced border-none" @click="saveMaterial" :loading="submitting" />
        </div>
      </template>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import MultiSelect from 'primevue/multiselect';
import ConfirmDialog from 'primevue/confirmdialog';
import Tooltip from 'primevue/tooltip';
import { useConfirm } from "primevue/useconfirm";
import { useToast, Divider } from 'primevue';
import { showToast } from '@/composables/toast';
import SellingMaterialService from '@/services/SellingMaterialService'; 
import MaterialListComponent from '@/components/Material/MaterialListComponent.vue';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';

const vTooltip = Tooltip;

const toast = useToast();
const confirm = useConfirm();

const groups = ref([]);
const allMaterials = ref([]);
const loading = ref(false);

const materialDialog = ref(false);
const groupDialog = ref(false);
const allMaterialsDialog = ref(false);

const submitting = ref(false);
const submittingGroup = ref(false);
const isDragging = ref(false);
const searchFilter = ref('');

const selectedMaterial = ref(null);
const selectedGroup = ref(null);
const selectedFile = ref(null);
const fileInput = ref(null);

const downloadingIds = ref(new Set());

const form = ref({
  name: '',
  description: ''
});

const groupForm = ref({
  title: '',
  price: 0,
  description: '',
  selling_material_ids: [],
  benefits: []
});

const allMaterialsOptions = computed(() => {
  return allMaterials.value.map(m => ({
    id: m.id,
    name: m.name
  }));
});

const unassignedMaterials = computed(() => {
  const assignedIds = new Set();
  groups.value.forEach(group => {
    if (Array.isArray(group.sellingMaterials)) {
      group.sellingMaterials.forEach(m => assignedIds.add(m.id));
    }
  });

  return allMaterials.value.filter(material => !assignedIds.has(material.id));
});

const filteredAllMaterials = computed(() => {
  if (!searchFilter.value.trim()) return allMaterials.value;
  const term = searchFilter.value.toLowerCase();
  return allMaterials.value.filter(m => 
    m.name.toLowerCase().includes(term) || 
    (m.description && m.description.toLowerCase().includes(term))
  );
});

onMounted(() => {
  loadData();
});

async function loadData(refresh = false) {
  loading.value = true;
  try {
    const [groupsRes, materialsRes] = await Promise.all([
      SellingMaterialService.getGroups(refresh),
      SellingMaterialService.getAllMaterials(refresh)
    ]);

    groups.value = Array.isArray(groupsRes) ? groupsRes : (groupsRes.data || []);
    allMaterials.value = Array.isArray(materialsRes) ? materialsRes : (materialsRes.data || []);

  } catch (error) {
    showToast(toast, 'Erro ao carregar materiais e pacotes.', 'error');
  } finally {
    loading.value = false;
  }
}

const openAllMaterialsModal = () => {
  searchFilter.value = '';
  allMaterialsDialog.value = true;
};

/* --- MANIPULAÇÃO DO ARRAY DE BENEFÍCIOS --- */
const addDetailItem = () => {
  groupForm.value.benefits.push('');
};

const removeDetailItem = (index) => {
  groupForm.value.benefits.splice(index, 1);
};

const openGroupModal = (group = null) => {
  if (group) {
    selectedGroup.value = group;

    // Tratamento para extrair os detalhes (pode vir como objeto, JSON string ou array)
    let parsedBenefits = [];
    if (group.benefits) {
      if (typeof group.benefits === 'string') {
        try {
          const json = JSON.parse(group.benefits);
          parsedBenefits = Array.isArray(json) ? json : Object.values(json);
        } catch (e) {
          parsedBenefits = [group.benefits];
        }
      } else if (Array.isArray(group.benefits)) {
        parsedBenefits = [...group.benefits];
      } else if (typeof group.benefits === 'object') {
        parsedBenefits = Object.values(group.benefits);
      }
    }

    groupForm.value = {
      title: group.title,
      price: Number(group.price) || 0,
      description: group.description || '',
      selling_material_ids: group.sellingMaterials ? group.sellingMaterials.map(m => m.id) : [],
      benefits: parsedBenefits
    };
  } else {
    selectedGroup.value = null;
    groupForm.value = {
      title: '',
      price: null,
      description: '',
      selling_material_ids: [],
      benefits: ['']
    };
  }
  groupDialog.value = true;
};

const saveGroup = async () => {
  if (!groupForm.value.title.trim()) {
    showToast(toast, 'O título do pacote é obrigatório', 'error');
    return;
  }

  if (groupForm.value.price === null || groupForm.value.price === undefined) {
    showToast(toast, 'O preço do pacote é obrigatório', 'error');
    return;
  }

  submittingGroup.value = true;

  const cleanDetails = groupForm.value.benefits
    .map(item => item.trim())
    .filter(item => item.length > 0);

  const payload = {
    ...(selectedGroup.value?.id && { id: selectedGroup.value.id }),
    title: groupForm.value.title.trim(),
    price: groupForm.value.price,
    description: groupForm.value.description.trim(),
    selling_material_ids: groupForm.value.selling_material_ids,
    benefits: JSON.stringify(cleanDetails)
  };

  try {
    let response = await SellingMaterialService.saveGroup(payload);
    const isSuccess = typeof response.success === 'function' ? response.success() : true;

    if (isSuccess) {
      showToast(toast, selectedGroup.value?.id ? 'Pacote atualizado!' : 'Pacote criado com sucesso!', 'success');
      groupDialog.value = false;
      loadData(true);
    } else {
      showToast(toast, response.getMessage ? response.getMessage() : 'Erro ao salvar pacote', 'error');
    }
  } catch (error) {
    showToast(toast, 'Erro ao processar o pacote.', 'error');
  } finally {
    submittingGroup.value = false;
  }
};

const confirmDeleteGroup = (group) => {
  confirm.require({
    message: `Tem certeza que deseja excluir o pacote "${group.title}"?`,
    header: 'Excluir Pacote',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Sim, excluir',
    rejectLabel: 'Cancelar',
    acceptClass: 'p-button-danger',
    accept: async () => {
      loading.value = true;
      try {
        const response = await SellingMaterialService.deleteGroup(group.id);
        const isSuccess = typeof response.success === 'function' ? response.success() : true;

        if (isSuccess) {
          showToast(toast, 'Pacote removido com sucesso.', 'success');
          loadData(true);
        } else {
          showToast(toast, response.getMessage(), 'error');
        }
      } catch (error) {
        showToast(toast, 'Erro ao deletar o pacote.', 'error');
      } finally {
        loading.value = false;
      }
    }
  });
};

/* --- MATERIAIS INDIVIDUAIS --- */
const openNewMaterial = () => {
  selectedMaterial.value = null;
  selectedFile.value = null;
  form.value = { 
    name: '', 
    description: ''
  };
  materialDialog.value = true;
};

const editMaterial = (material) => {
  selectedMaterial.value = material;
  selectedFile.value = null;
  form.value = { 
    name: material.name, 
    description: material.description || ''
  };
  materialDialog.value = true;
};

const onFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;
    if (!form.value.name) {
      form.value.name = file.name;
    }
  }
};

const onFileDrop = (event) => {
  isDragging.value = false;
  const files = event.dataTransfer.files;
  if (files && files.length > 0) {
    selectedFile.value = files[0];
    if (!form.value.name) {
      form.value.name = files[0].name;
    }
  }
};

const downloadFile = async (material) => {
  downloadingIds.value.add(material.id);
  try {
    const response = await SellingMaterialService.fetchDownloadData(material.id);
    
    if (typeof response.success === 'function' && !response.success()) {
      showToast(toast, response.getMessage(), 'error');
      return;
    }

    const blobData = typeof response.getContent === 'function' ? response.getContent() : response.data;
    const url = window.URL.createObjectURL(new Blob([blobData]));
    const link = document.createElement('a');
    link.href = url;
    
    const ext = typeof material.getExtension === 'function' 
      ? material.getExtension() 
      : '';

    link.setAttribute('download', material.name + ext);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (e) {
    showToast(toast, 'Erro ao realizar o download do arquivo.', 'error');
  } finally {
    downloadingIds.value.delete(material.id);
  }
};

const saveMaterial = async () => {
  if (!form.value.name.trim()) {
    showToast(toast, 'O campo nome é obrigatório', 'error');
    return;
  }

  if (!selectedMaterial.value?.id && !selectedFile.value) {
    showToast(toast, 'Selecione um arquivo para upload', 'error');
    return;
  }

  submitting.value = true;

  const formData = new FormData();
  if (selectedMaterial.value?.id) {
    formData.append('id', selectedMaterial.value.id);
  }
  formData.append('name', form.value.name.trim());
  formData.append('description', form.value.description.trim());

  if (selectedFile.value) {
    formData.append('file', selectedFile.value);
  }

  try {
    let response = await SellingMaterialService.save(formData);
    const isSuccess = typeof response.success === 'function' ? response.success() : true;

    if (isSuccess) {
      showToast(toast, selectedMaterial.value?.id ? 'Material atualizado!' : 'Material criado!', 'success');
      materialDialog.value = false;
      loadData(true);
    } else {
      showToast(toast, response.getMessage ? response.getMessage() : 'Erro ao salvar', 'error');
    }
  } catch (error) {
    showToast(toast, 'Erro ao salvar o material.', 'error');
  } finally {
    submitting.value = false;
  }
};

const confirmDelete = (material) => {
  confirm.require({
    message: `Tem certeza que deseja excluir permanentemente o material "${material.name}"?`,
    header: 'Confirmação de Exclusão',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Sim, excluir',
    rejectLabel: 'Cancelar',
    acceptClass: 'p-button-danger',
    accept: async () => {
      loading.value = true;
      try {
        const response = await SellingMaterialService.delete(material.id);
        const isSuccess = typeof response.success === 'function' ? response.success() : true;

        if (isSuccess) {
          showToast(toast, 'Material removido com sucesso.', 'success');
          loadData(true);
        } else {
          showToast(toast, response.getMessage(), 'error');
        }
      } catch (error) {
        showToast(toast, 'Erro ao deletar o material.', 'error');
      } finally {
        loading.value = false;
      }
    }
  });
};
</script>